export const ENALYSIS_METRICS_CATEGORY = "Enalysis Metrics";
export const ENALYSIS_BLOWBY_CATEGORY = "Blowby";
export const ENALYSIS_UTILIZATION_CATEGORY = "Utilization";
export const ENALYSIS_ROD_LOADS_CATEGORY = "Rod Loads";

export const ENALYSIS_FILTER_ALL_SENTINEL = "__enalysis_all__";

export const ENALYSIS_TRENDS_DATA_RANGE_MONTHS = 1;

export const ENALYSIS_TRENDS_DATA_RANGE_MONTHS_MAX = 12;

/** Max trend identifiers per `/Trends/...` GET `Keys` query (URL length limits). */
export const ENALYSIS_TRENDS_KEYS_MAX_PER_REQUEST = 35;

/** Max length of comma-joined `Keys` string per request before splitting into another GET. */
export const ENALYSIS_TRENDS_KEYS_MAX_CHUNK_CHARS = 1400;

export const ENALYSIS_TREND_LIST_SOURCE = "enalysis-trends-list";

export const ENALYSIS_TREND_ROW_CATEGORIES = new Set([
  ENALYSIS_METRICS_CATEGORY,
  ENALYSIS_BLOWBY_CATEGORY,
  ENALYSIS_UTILIZATION_CATEGORY,
  ENALYSIS_ROD_LOADS_CATEGORY,
]);

export const ENALYSIS_ZERO_TO_HUNDRED_GAUGE_BASE_IDS = new Set([
  "compressorEfficiency",
  "mechanicalEfficiency",
]);

export function isEnalysisZeroToHundredBandParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  if (param?.category === ENALYSIS_UTILIZATION_CATEGORY) return true;
  const base = param.apiData.baseTrendIdentifier ?? param.apiData.trendIdentifier;
  if (typeof base !== "string" || !base) return false;
  return ENALYSIS_ZERO_TO_HUNDRED_GAUGE_BASE_IDS.has(base);
}

export const ENALYSIS_VOLUME_EFFICIENCY_GAUGE_BASE_IDS = new Set([
  "volumeEfficiencyHeadEnd",
  "volumeEfficiencyCrankEnd",
]);

export function isEnalysisVolumeEfficiencyBandParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  const base = param.apiData.baseTrendIdentifier ?? param.apiData.trendIdentifier;
  if (typeof base !== "string" || !base) return false;
  return ENALYSIS_VOLUME_EFFICIENCY_GAUGE_BASE_IDS.has(base);
}

export const ENALYSIS_ROD_LOAD_PERCENT_GAUGE_BASE_IDS = new Set([
  "intExtRodLoadCompressionMaxPercent",
  "intExtRodLoadTensionMaxPercent",
  "netRodLoadCompressionMaxPercent",
  "netRodLoadTensionMaxPercent",
  "totalInternalRodLoadMaxPercent",
]);

export function isEnalysisRodLoadPercentBandParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  const base = param.apiData.baseTrendIdentifier ?? param.apiData.trendIdentifier;
  if (typeof base !== "string" || !base) return false;
  return ENALYSIS_ROD_LOAD_PERCENT_GAUGE_BASE_IDS.has(base);
}

export const ENALYSIS_MIN_DEGREES_REVERSAL_GAUGE_BASE_IDS = new Set([
  "minDegreesReversal",
]);

export function isEnalysisMinDegreesReversalBandParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  const base = param.apiData.baseTrendIdentifier ?? param.apiData.trendIdentifier;
  if (typeof base !== "string" || !base) return false;
  return ENALYSIS_MIN_DEGREES_REVERSAL_GAUGE_BASE_IDS.has(base);
}

export const ENALYSIS_MIN_NET_ROD_LOAD_RATIO_GAUGE_BASE_IDS = new Set([
  "minNetRodLoadRatio",
]);

export function isEnalysisMinNetRodLoadRatioBandParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  const base = param.apiData.baseTrendIdentifier ?? param.apiData.trendIdentifier;
  if (typeof base !== "string" || !base) return false;
  return ENALYSIS_MIN_NET_ROD_LOAD_RATIO_GAUGE_BASE_IDS.has(base);
}

export const ENALYSIS_CLEARANCE_ZERO_TO_MAX_GAUGE_BASE_IDS = new Set([
  "hePocketAdjustment",
  "headEndSpacersAdded",
  "crankEndSpacersAdded",
]);

/** Clearance / spacer trends: red below 0, green 0..max from clearances API, red above max. */
export function isEnalysisClearanceZeroToMaxParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  if (param.apiData?.gaugeLayout !== "clearanceZeroToMax") return false;
  const maxV = parseFloat(param.apiData?.maxValue);
  return Number.isFinite(maxV) && maxV > 0;
}

const PRESSURE_UNIT_SETUP_GAUGE_LAYOUTS = new Set([
  "dischargePressureUnitSetup",
  "suctionPressureUnitSetup",
]);

/** Discharge or suction pressure: 90%/95% bands, unit-setup scale (suction uses ref-stage discharge limits). */
export function isEnalysisDischargeOrSuctionPressureUnitSetupParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  if (!PRESSURE_UNIT_SETUP_GAUGE_LAYOUTS.has(param.apiData?.gaugeLayout)) {
    return false;
  }
  const maxV = parseFloat(param.apiData?.maxValue);
  return Number.isFinite(maxV) && maxV > 0;
}

const LOW_TO_HIGH_UNIT_SETUP_GAUGE_LAYOUTS = new Set([
  "dischargeTemperatureUnitSetup",
  "blowbyUnitSetup",
]);

/** Discharge temperature or blowby: extended min/max domain, green / yellow / red by alarm hi/hihi. */
export function isEnalysisLowToHighBandUnitSetupParam(param) {
  if (param?.apiData?.source !== ENALYSIS_TREND_LIST_SOURCE) return false;
  if (!LOW_TO_HIGH_UNIT_SETUP_GAUGE_LAYOUTS.has(param.apiData?.gaugeLayout)) {
    return false;
  }
  const maxV = parseFloat(param.apiData?.maxValue);
  return Number.isFinite(maxV) && maxV > 0;
}

export const ENALYSIS_TRENDS_LIST_ALLOWED_IDENTIFIERS = new Set([
  "cylinderCapacityUtilized",
  "horsepowerUtilized",
  "horsepowerUtilizedRpmDerated",
  "engineHorsepowerUsed",
  "totalAuxiliaryPowerUsed",
  "stationInletPressure",
  "compressorUnitFlow",
  "fuelGasUsed",
  "co2Emission",
  "co2eEmission",
  "noxEmission",
  "turboBoostVacuumPsig",
  "turboBoostVacuumPercent",
  "incrementalProduction",
  "cfr",
  "horsepowerRanking",
  "blowbyRanking",
  "productionRanking",
  "damageRanking",
  "downtimeRanking",
  "suctionPressure",
  "dischargePressure",
  "suctionTemperature",
  "dischargeTemperature",
  "expectedDischargeTemperature",
  "temperatureRise",
  "compressionRatio",
  "cylinderFlow",
  "horsepowerCylinder",
  "blowby",
  "compressorEfficiency",
  "mechanicalEfficiency",
  "volumeEfficiencyHeadEnd",
  "volumeEfficiencyCrankEnd",
  "intExtRodLoadCompressionMaxPercent",
  "intExtRodLoadTensionMaxPercent",
  "netRodLoadCompressionMaxPercent",
  "netRodLoadTensionMaxPercent",
  "totalInternalRodLoadMaxPercent",
  "minDegreesReversal",
  "minNetRodLoadRatio",
  "runClearanceHeadEnd",
  "runClearanceCrankEnd",
  "hePocketAdjustment",
  "headEndSpacersAdded",
  "crankEndSpacersAdded",
]);
